import { IDateNumber } from 'src/types';
import { date } from 'quasar';
// destructuring to keep only what is needed
const { getDateDiff, isValid, addToDate, formatDate, buildDate, extractDate } =
  date;
import { i18n } from 'boot/i18n';
const t = i18n.global.t;

function makeDateFromNumber(dateNumber: IDateNumber): string {
  return formatDate(
    buildDate({
      year: dateNumber.year,
      month: dateNumber.month,
      day: dateNumber.day,
    }),
    'YYYY/MM/DD',
  );
}

function calculateNumberOfNights(from: string, to: string): number {
  let num = 1;
  if (isValid(from) && isValid(to)) {
    num = getDateDiff(to, from, 'days');
  }
  return num;
}

function calculateNumberOfNightsDisplay(from: string, to: string): string {
  const nights = calculateNumberOfNights(from, to);
  return formatNumberOfNightsDisplay(nights);
}

function calculateToDate(from: string, numberOfNights: number): string {
  return formatDate(addToDate(from, { days: numberOfNights }), 'YYYY/MM/DD');
}

function formatNumberOfNightsDisplay(numberOfNights: number): string {
  const nights = numberOfNights;
  if (nights == 1) {
    return nights + ' ' + t('night');
  } else if (nights > 1) {
    return nights + ' ' + t('nights');
  }
  return nights + ' ' + t('nights');
}

function formatDateDisplay(
  dateValue: string,
  formatString: string = 'ddd Do MMM YYYY',
): string {
  let v = '';
  if (dateValue != '' && dateValue != null) {
    v = formatDate(dateValue, formatString);
  }
  return v;
}

/**
 * Compare two dates in the format 'YYYY/MM/DD'
 *
 * @remarks
 *
 * @param a - The first input date in 'YYYY/MM/DD'
 * @param b - The second input date in 'YYYY/MM/DD'
 * @returns a string result of "Same", "Before", "After"
 *
 */
function compareDates(a: string, b: string) {
  let result = '';
  if (a != null && a != '' && b != null && b != '') {
    const [year1, month1, day1] = a.split('/').map(Number);
    const [year2, month2, day2] = b.split('/').map(Number);

    const d1 = new Date(year1, month1 - 1, day1);
    const d2 = new Date(year2, month2 - 1, day2);

    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);

    const timeDiff = d1.getTime() - d2.getTime();

    if (timeDiff < 0) {
      result = 'Before';
    } else if (timeDiff > 0) {
      result = 'After';
    } else {
      result = 'Same';
    }
  }

  return result;
}

/*
Inspired by Moment.js

https://github.com/moment/moment/blob/18aba135ab927ffe7f868ee09276979bed6993a6/src/lib/moment/start-end-of.js#L34
*/
function startOf(
  year: number,
  month: number,
  day: number,
  units: 'date' | 'day' | 'month' | 'year' = 'day',
) {
  let d: Date | null = null;
  switch (units) {
    case 'year':
      d = addToDate(Date.UTC(year, 0, 1), { day: 0 });
      break;
    case 'month':
      d = addToDate(Date.UTC(year, month - 1, 1), { day: 0 });
      break;
    case 'day':
    case 'date':
      d = addToDate(Date.UTC(year, month - 1, day), { day: 0 });
      break;
    default:
      break;
  }
  d.setUTCHours(0, 0, 0, 0);
  return d;
}

/*
Inspired by Moment.js

https://github.com/moment/moment/blob/18aba135ab927ffe7f868ee09276979bed6993a6/src/lib/moment/start-end-of.js#L97
*/
function endOf(
  year: number,
  month: number,
  day: number,
  units: 'date' | 'day' | 'month' | 'year' = 'day',
) {
  let d: Date | null = null;
  switch (units) {
    case 'year':
      d = addToDate(Date.UTC(year + 1, 0, 1), { day: -1 });
      break;
    case 'month':
      // first of the next month, then subtract a day
      d = addToDate(Date.UTC(year, month, 1), { day: -1 });
      break;
    case 'day':
    case 'date':
      d = addToDate(Date.UTC(year, month - 1, day + 1), { day: -1 });
      break;
    default:
      break;
  }
  d.setUTCHours(0, 0, 0, 0);

  return d;
}

function isBefore(
  a: string,
  b: string,
  comparison: 'date' | 'day' | 'month' | 'year' = 'day',
): boolean {
  if (a == null || a == undefined || a == '') {
    return false;
  }

  if (b == null || b == undefined || b == '') {
    return false;
  }

  const [year1, month1, day1] = a.split(/-|\//).map(Number);
  const [year2, month2, day2] = b.split(/-|\//).map(Number);

  const d1 = endOf(year1, month1, day1, comparison);
  const d2 = endOf(year2, month2, day2, comparison);

  return d1.valueOf() < d2.valueOf();
}

function isAfter(
  a: string,
  b: string,
  comparison: 'date' | 'day' | 'month' | 'year' = 'day',
): boolean {
  if (a == null || a == undefined || a == '') {
    return false;
  }

  if (b == null || b == undefined || b == '') {
    return false;
  }

  const [year1, month1, day1] = a.split(/-|\//).map(Number);
  const [year2, month2, day2] = b.split(/-|\//).map(Number);

  const d1 = startOf(year1, month1, day1, comparison);
  const d2 = startOf(year2, month2, day2, comparison);

  return d1.valueOf() > d2.valueOf();
}

function isSame(
  a: string,
  b: string,
  comparison: 'date' | 'day' | 'month' | 'year' = 'day',
): boolean {
  if (a == null || a == undefined || a == '') {
    return false;
  }

  if (b == null || b == undefined || b == '') {
    return false;
  }
  const [year1, month1, day1] = a.split(/-|\//).map(Number);
  const [year2, month2, day2] = b.split(/-|\//).map(Number);

  const d1_start = startOf(year1, month1, day1, comparison);
  const d2_start = startOf(year2, month2, day2, comparison);
  const d2_end = endOf(year2, month2, day2, comparison);

  return (
    d2_start.valueOf() <= d1_start.valueOf() &&
    d1_start.valueOf() <= d2_end.valueOf()
  );
}

function isSameOrAfterDate(a: string, b: string): boolean {
  const result = compareDates(a, b);
  return result == 'Same' || result == 'After';
}

function isSameOrBeforeDate(a: string, b: string): boolean {
  const result = compareDates(a, b);
  return result == 'Same' || result == 'Before';
}

function isSameOrAfterMonth(a: string, b: string): boolean {
  return isSame(a, b, 'month') || isAfter(a, b, 'month');
}

function isSameOrBeforeMonth(a: string, b: string): boolean {
  return isSame(a, b, 'month') || isBefore(a, b, 'month');
}

function isSameMonth(a: string, b: string): boolean {
  return isSame(a, b, 'month');
}

function isSameDate(a: string, b: string): boolean {
  return isSame(a, b, 'day');
}

function isBeforeToday(a: string): boolean {
  const today = formatDate(new Date(), 'YYYY/MM/DD');
  return isBefore(a, today, 'month');
}

/**
 * Convert date from one format to another
 *
 * @remarks
 *
 * @param inputDate - the date in string format
 * @param startFormat - the format of the date
 * @param endFormat - the target format
 * @returns the date value formated in the target format
 *
 */
function convertDate(
  inputDate: string,
  startFormat: string,
  endFormat: string,
): string {
  const inputDateStr = extractDate(inputDate, startFormat);
  const formattedDate = formatDate(inputDateStr, endFormat);
  return formattedDate;
}

function get1stOfNextMonth() {
  const today = new Date();
  const nextMonth = formatDate(
    new Date(today.getFullYear(), today.getMonth() + 1, 1),
    'YYYY/MM/DD',
  );
  return nextMonth;
}

function today(): string {
  return formatDate(new Date(), 'YYYY/MM/DD');
}

export default function useDateHelperUtils() {
  return {
    today: today,
    calculateToDate: calculateToDate,
    isSameOrBeforeDate: isSameOrBeforeDate,
    isSameOrBeforeMonth: isSameOrBeforeMonth,
    isSameOrAfterMonth: isSameOrAfterMonth,
    isSameOrAfterDate: isSameOrAfterDate,
    isSameDate: isSameDate, // Ie is it equal date.
    isSameMonth: isSameMonth,
    isBeforeToday: isBeforeToday,
    isBefore: isBefore,
    isAfter: isAfter,
    isSame: isSame,
    convertDate: convertDate,
    makeDateFromNumber: makeDateFromNumber,
    formatNumberOfNightsDisplay: formatNumberOfNightsDisplay,
    formatDateDisplay: formatDateDisplay,
    calculateNumberOfNights: calculateNumberOfNights,
    calculateNumberOfNightsDisplay: calculateNumberOfNightsDisplay,
    get1stOfNextMonth: get1stOfNextMonth,
  };
}
